import { useState } from "react";
import ReactGA from "react-ga4";
import { addPatientAction } from "store/actions/patients";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useFetchPatients, useLogout, useCurrentUser } from "utils/hooks";
import { useNavigate } from "react-router-dom";
import useOrganizationSettings, {
  getPatientWordCapitalized,
  getPatientWordPlural,
} from "utils/hooks/organizations/useOrganizationSettings";

export interface AddPatientArgs {
  first_name: string;
  last_name: string;
  email: string;
  dob: string;
  gender?: {
    value: string;
  };
  groups?: GroupOption[];
  idNumber: string;
  sendInvite?: boolean;
}
interface GroupOption {
  value: string;
  label: string;
}

const useAddPatient = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const [newPatient, setNewPatient] = useState<any>(null);
  const dispatch = useDispatch();
  const history = useNavigate();
  const { logOut } = useLogout();
  const { fetchAllPatients } = useFetchPatients();
  const { user } = useCurrentUser();
  const { t } = useTranslation(["common"]);
  const { settings: orgSettings } = useOrganizationSettings();

  const formatGroups = (groups: GroupOption[] | undefined) => {
    return groups ? groups.map((group) => `"${group.value}"`) : [];
  };

  const submitAddPatient = (
    args: AddPatientArgs | null,
    redirectToNewPatient?: boolean
  ): Promise<boolean | void> => {
    if (!args) {
      return Promise.resolve();
    }

    setIsSubmitting(true);

    return new Promise((resolve) => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                addPatient(patient: {
                  firstName: "${args.first_name}",
                  lastName: "${args.last_name}",
                  email: "${args.email}",
                  dob: "${args.dob}",
                  gender: "${args.gender?.value}",
                  groupIds: [${formatGroups(args?.groups)}],
                  idNumber: "${args.idNumber}",
                  sendInvite: ${args.sendInvite === false ? false : true}
                }) {
                  uuid
                  email
                  firstName
                  lastName
                  slug
                  createdAt
                  profile {
                    dob
                    gender
                    status
                    idNumber
                  }
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "add_patients",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn("For security purposes please log in again.");
                logOut();
              }
              if (error.errors[0].message.includes("PATIENT_LIMIT_REACHED")) {
                toast.warn(
                  t("errors.patient_limit_reached", {
                    patientWordPlural: getPatientWordPlural(orgSettings),
                  })
                );
                setIsSubmitSuccessful(true);
                resolve();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              resolve();
              return true;
            } else {
              fetchAllPatients();
              toast(
                t("inbox.patient_added_success", {
                  patientWordCapitalized:
                    getPatientWordCapitalized(orgSettings),
                })
              );
              setNewPatient(resData.data.addPatient);
              setIsSubmitSuccessful(true);
              resolve();
              ReactGA.event("patient_added_success", {
                user_id: user?.id,
                patient_id: resData.data.addPatient.uuid,
              });
              dispatch(addPatientAction(resData.data.addPatient));
              if (!redirectToNewPatient) {
                return resData.data.addPatient;
              }
              return history(
                `/patients/${resData.data.addPatient.slug}/workbook`
              );
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      }, 100);
    });
  };

  return {
    submitAddPatient,
    isSubmitting,
    isSubmitSuccessful,
    newPatient,
  };
};

export default useAddPatient;
